import React, { useState, useMemo } from 'react';
import { BookOpen, ChevronRight, Search } from 'lucide-react';
import { Button, Accordion, AccordionItem, Input } from '@heroui/react';
import { cn } from '@/utils/cn';
import { GuideStepType, GuideStep } from '@/services/apis/guides';
import { Glossary } from '@/services/apis/glossaries';

interface GuideViewerGlossariesProps {
  currentStep: GuideStep | null;
  isOpen: boolean;
  onToggle: () => void;
  currentLanguage: string;
}

const GuideViewerGlossaries: React.FC<GuideViewerGlossariesProps> = ({
  currentStep,
  isOpen,
  onToggle,
  currentLanguage,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Get glossaries from step
  const stepGlossaries = useMemo(() => {
    return currentStep?.glossaries || [];
  }, [currentStep]);

  // Filter glossaries based on search query
  const filteredGlossaries = useMemo(() => {
    if (!searchQuery.trim()) {
      return stepGlossaries;
    }

    const query = searchQuery.toLowerCase();
    return stepGlossaries.filter((glossary: Glossary) => {
      const nameMatch = glossary.name.toLowerCase().includes(query);
      const aliasMatch = glossary.aliases.some((alias) => alias.toLowerCase().includes(query));
      const translationMatch = glossary.translations?.some((t: any) => t.definition?.toLowerCase().includes(query));
      return nameMatch || aliasMatch || translationMatch;
    });
  }, [stepGlossaries, searchQuery]);

  // Only show for Rich Text steps
  if (currentStep?.type !== GuideStepType.RICH_TEXT) {
    return null;
  }

  return (
    <div className={cn('hidden lg:flex flex-col transition-all duration-300', isOpen ? 'w-64' : 'w-12')}>
      <div className="bg-content1 rounded-lg border border-divider h-full overflow-hidden flex flex-col">
        <div
          className={`flex items-center ${isOpen ? 'justify-between' : 'justify-center'} p-4 border-b border-divider`}
        >
          {isOpen && (
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-foreground-500" />
              <h3 className="text-sm font-semibold text-foreground-500 uppercase tracking-wide">Glossary</h3>
            </div>
          )}
          <Button
            isIconOnly
            size="sm"
            variant="light"
            onPress={onToggle}
            aria-label={isOpen ? 'Collapse glossary' : 'Expand glossary'}
          >
            {isOpen ? <ChevronRight className="w-4 h-4" /> : <BookOpen className="w-4 h-4" />}
          </Button>
        </div>
        {isOpen && (
          <>
            {stepGlossaries.length > 0 && (
              <div className="p-3 border-b border-divider">
                <Input
                  size="sm"
                  placeholder="Search glossaries..."
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                  startContent={<Search className="w-4 h-4 text-foreground-400" />}
                  classNames={{
                    input: 'text-sm',
                    inputWrapper: 'h-9',
                  }}
                />
              </div>
            )}
            <div className="flex-1 overflow-y-auto py-3 px-1">
              {filteredGlossaries.length > 0 ? (
                <Accordion selectionMode="multiple" variant="splitted">
                  {filteredGlossaries.map((glossary: Glossary) => {
                    const translation =
                      glossary.translations?.find((t: any) => t.language === currentLanguage) ||
                      glossary.translations?.[0];
                    return (
                      <AccordionItem key={glossary.id} title={glossary.name} aria-label={glossary.name}>
                        <div className="text-sm text-foreground-600 dark:text-foreground-400">
                          {translation?.definition || 'No definition available'}
                        </div>
                        {glossary.aliases && glossary.aliases.length > 0 && (
                          <div className="mt-2">
                            <span className="text-xs font-medium text-foreground-500">Also known as: </span>
                            <span className="text-xs text-foreground-600 dark:text-foreground-400">
                              {glossary.aliases.join(', ')}
                            </span>
                          </div>
                        )}
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              ) : searchQuery ? (
                <div className="text-center text-foreground-500 py-8 text-sm">No glossaries match your search</div>
              ) : (
                <div className="text-center text-foreground-500 py-8 text-sm">No glossary terms found in this step</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GuideViewerGlossaries;
